import type { PropertyCursor } from '@/api/core/properties';
import { isOffsetDateTime, isUuid } from '@/lib/validation';

const accessQueryKeys = [
  'before_created_at',
  'before_property_id',
  'room_cursor',
  'room',
  'view',
  'device_sort',
  'device_direction',
  'device_cursor',
  'device_q',
  'device',
] as const;

export function deviceLedgerState(params: URLSearchParams) {
  const sortBy = params.get('device_sort');
  const direction = params.get('device_direction');
  const cursor = params.get('device_cursor');
  const deviceId = params.get('device');
  const valid =
    ['device_sort', 'device_direction', 'device_cursor', 'device_q', 'device'].every(
      (key) => params.getAll(key).length <= 1,
    ) &&
    ((sortBy === null && direction === null) ||
      ([
        'device_id',
        'gateway_id',
        'device_name',
        'gateway_name',
        'device_type',
        'battery',
        'is_active',
      ].includes(sortBy ?? '') &&
        ['asc', 'desc'].includes(direction ?? ''))) &&
    (cursor === null || cursor.length > 0) &&
    (deviceId === null || isUuid(deviceId));
  return {
    valid,
    sort: sortBy ? `${sortBy},${direction ?? ''}` : null,
    cursor,
    search: params.get('device_q') ?? '',
    deviceId,
  };
}

export function accessUrlState(params: URLSearchParams) {
  const beforeCreatedAt = params.get('before_created_at');
  const beforePropertyId = params.get('before_property_id');
  const roomCursor = params.get('room_cursor');
  const roomId = params.get('room');
  const duplicate = accessQueryKeys.some((key) => params.getAll(key).length > 1);
  const propertyCursorValid =
    (beforeCreatedAt === null && beforePropertyId === null) ||
    (isOffsetDateTime(beforeCreatedAt) && isUuid(beforePropertyId));
  const detailValid =
    (roomCursor === null || isUuid(roomCursor)) && (roomId === null || isUuid(roomId));
  const allowed = [...params.keys()].every((key) =>
    accessQueryKeys.includes(key as (typeof accessQueryKeys)[number]),
  );
  return {
    valid: !duplicate && propertyCursorValid && detailValid && allowed,
    propertyCursor:
      propertyCursorValid && beforeCreatedAt && beforePropertyId
        ? ({
            before_created_at: beforeCreatedAt,
            before_property_id: beforePropertyId,
          } satisfies PropertyCursor)
        : null,
    roomCursor,
    roomId,
  };
}

export function accessHref(propertyId: string) {
  return `/access/${propertyId}`;
}

export function accessPropertiesHref(cursor: PropertyCursor | null) {
  return cursor
    ? `/access?${new URLSearchParams(cursor as unknown as Record<string, string>)}`
    : '/access';
}

export function accessRoomCursorHref(propertyId: string, cursor: string | null) {
  return cursor
    ? `/access/${propertyId}?${new URLSearchParams({ room_cursor: cursor })}`
    : `/access/${propertyId}`;
}
